import React, { useEffect, useState, useRef } from "react";

// Custom hook for responsive design
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkMobile();

    // Listen for window resize
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return { isMobile };
};

// Announcement shape sent by backend, simplified for rendering
interface Announcement {
  id: string;
  variant: "info" | "warning" | "success" | "danger" | "promo";
  title: string;
  body: string;
  end_at?: string | null;
  cta?: { text?: string; url?: string } | null;
  is_dismissible: boolean;
  sticky?: boolean;
}

interface Props {
  location?: string; // e.g., "global", "home", "pricing", "chat"
  userId?: string; // Optional: pass when authenticated so server can exclude dismissed
  onHeightChange?: (height: number) => void; // Optional: callback for height changes
}

/**
 * AnnouncementBanner
 *
 * Fetches active announcement(s) for a given location and displays the
 * highest-priority banner. Dismissals are persisted server-side for
 * authenticated users and in localStorage for anonymous users.
 */
const AnnouncementBanner: React.FC<Props> = ({
  location = "global",
  userId,
  onHeightChange,
}) => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        setLoading(true);
        const api = process.env.REACT_APP_API_URL || "";
        const lang = (navigator.language || "en").slice(0, 2);
        const qs = new URLSearchParams({ location, lang });
        if (userId) qs.set("user_id", userId);

        const res = await fetch(
          `${api}/api/announcements/active?${qs.toString()}`,
          {
            signal: controller.signal,
          }
        );
        if (!res.ok) {
          setLoading(false);
          return;
        }
        const data = await res.json();
        const first: Announcement | undefined = (data?.announcements || [])[0];
        if (!first) {
          setAnnouncement(null);
          setLoading(false);
          return;
        }
        // Anonymous dismissal check
        const lsKey = `ann_dismiss_${first.id}`;
        if (
          typeof window !== "undefined" &&
          window.localStorage?.getItem(lsKey)
        ) {
          setAnnouncement(null);
        } else {
          setAnnouncement(first);
        }
      } catch (_) {
        // Non-blocking: silently ignore errors
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => controller.abort();
  }, [location, userId]);

  // Effect to measure banner height and notify parent/global styles
  useEffect(() => {
    if (bannerRef.current) {
      const height = announcement ? bannerRef.current.offsetHeight : 0;

      // Update CSS variable for fixed elements (like Header)
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);

      if (onHeightChange) {
        onHeightChange(height);
      }
    } else if (!announcement) {
      document.documentElement.style.setProperty('--announcement-height', '0px');
    }
  }, [announcement, onHeightChange, isMobile]); // Re-measure on mobile state change

  // Reset on unmount
  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty('--announcement-height', '0px');
    };
  }, []);

  if (loading || !announcement) return null;

  const onDismiss = async () => {
    try {
      // Save server-side dismissal when logged-in
      if (userId) {
        const api = process.env.REACT_APP_API_URL || "";
        await fetch(`${api}/api/announcements/${announcement.id}/dismiss`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
      }
    } catch (_) {
      // Ignore server errors; client-side dismiss still applies
    } finally {
      try {
        const lsKey = `ann_dismiss_${announcement.id}`;
        window.localStorage?.setItem(lsKey, "1");
      } catch (_) { }
      setAnnouncement(null);
      // Notify parent about height change when dismissed
      if (onHeightChange) {
        onHeightChange(0);
      }
    }
  };

  // Premium moodboard-inspired palette with gradients
  const palette: Record<
    string,
    { bg: string; gradient: string; txt: string; accent: string; border: string; ctaBg: string }
  > = {
    info: {
      bg: "rgba(24, 67, 66, 0.98)",
      gradient: "linear-gradient(135deg, rgba(24, 67, 66, 0.98) 0%, rgba(32, 91, 90, 0.98) 100%)",
      txt: "#ffffff",
      accent: "rgba(112, 205, 203, 1)",
      border: "rgba(45, 127, 125, 0.5)",
      ctaBg: "rgba(255, 255, 255, 0.15)",
    },
    promo: {
      bg: "rgba(24, 67, 66, 0.98)",
      gradient: "linear-gradient(135deg, rgba(32, 91, 90, 0.98) 0%, rgba(57, 163, 161, 0.95) 100%)",
      txt: "#ffffff",
      accent: "rgba(183, 230, 229, 1)",
      border: "rgba(76, 192, 190, 0.4)",
      ctaBg: "rgba(255, 255, 255, 0.2)",
    },
    warning: {
      bg: "rgba(248, 155, 7, 0.95)",
      gradient: "linear-gradient(135deg, rgba(250, 173, 51, 0.95) 0%, rgba(248, 155, 7, 0.95) 100%)",
      txt: "#184342",
      accent: "#184342",
      border: "rgba(204, 127, 5, 0.4)",
      ctaBg: "rgba(24, 67, 66, 0.12)",
    },
    success: {
      bg: "rgba(45, 127, 125, 0.98)",
      gradient: "linear-gradient(135deg, rgba(57, 163, 161, 0.95) 0%, rgba(45, 127, 125, 0.98) 100%)",
      txt: "#ffffff",
      accent: "rgba(183, 230, 229, 1)",
      border: "rgba(76, 192, 190, 0.4)",
      ctaBg: "rgba(255, 255, 255, 0.15)",
    },
    danger: {
      bg: "rgba(186, 86, 86, 0.98)",
      gradient: "linear-gradient(135deg, rgba(200, 120, 120, 0.95) 0%, rgba(159, 65, 65, 0.98) 100%)",
      txt: "#ffffff",
      accent: "rgba(255, 219, 67, 1)",
      border: "rgba(159, 65, 65, 0.5)",
      ctaBg: "rgba(255, 255, 255, 0.15)",
    },
  };

  const colors = palette[announcement.variant] || palette.info;
  const expiryText = (() => {
    if (announcement.variant !== "promo" || !announcement.end_at) return "";

    const date = new Date(announcement.end_at);
    if (Number.isNaN(date.getTime())) return "";

    const day = date.getDate();
    const suffix =
      day % 100 >= 11 && day % 100 <= 13
        ? "th"
        : day % 10 === 1
          ? "st"
          : day % 10 === 2
            ? "nd"
            : day % 10 === 3
              ? "rd"
              : "th";
    const month = date.toLocaleString("en-US", { month: "long" });
    return `Ends ${month} ${day}${suffix}`;
  })();
  const bodyText = [announcement.body, expiryText].filter(Boolean).join(" ");

  // Responsive styles - premium moodboard design
  const containerStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1001,
    width: "100%",
    background: colors.gradient,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: "'Raleway', sans-serif",
    boxShadow: "0 2px 12px rgba(32, 91, 90, 0.15)",
  };

  const innerContainerStyle: React.CSSProperties = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    minHeight: 48,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    textAlign: "center",
  };

  const textContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  };

  const titleStyle: React.CSSProperties = {
    fontWeight: 700,
    color: colors.txt,
    lineHeight: 1.3,
    fontSize: 14,
    letterSpacing: "-0.01em",
  };

  const bodyStyle: React.CSSProperties = {
    color: colors.txt,
    opacity: 0.9,
    fontSize: 13,
    lineHeight: 1.4,
    fontWeight: 500,
  };

  // CTA as pill button instead of underlined link
  const ctaStyle: React.CSSProperties = {
    color: colors.txt,
    backgroundColor: colors.ctaBg,
    borderRadius: "100px",
    padding: "6px 16px",
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    textDecoration: "none",
    border: `1px solid ${colors.accent}30`,
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };

  const dismissButtonStyle: React.CSSProperties = {
    background: "rgba(255, 255, 255, 0.1)",
    border: "none",
    cursor: "pointer",
    color: colors.txt,
    fontSize: 18,
    lineHeight: 1,
    padding: 8,
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    flexShrink: 0,
    transition: "all 0.2s ease",
    opacity: 0.7,
  };

  // Apply mobile-specific styles
  if (isMobile) {
    innerContainerStyle.padding = "10px 16px";
    innerContainerStyle.gap = 12;
    innerContainerStyle.minHeight = 44;

    textContainerStyle.gap = 8;
    textContainerStyle.flexDirection = "column";
    textContainerStyle.alignItems = "center";

    titleStyle.fontSize = 13;
    bodyStyle.fontSize = 12;
    ctaStyle.fontSize = 12;
    ctaStyle.padding = "5px 14px";
    dismissButtonStyle.fontSize = 16;
    dismissButtonStyle.width = 28;
    dismissButtonStyle.height = 28;
  }

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Site announcement"
      style={containerStyle}
    >
      <div style={innerContainerStyle}>
        <div style={contentStyle}>
          <div style={textContainerStyle}>
            {announcement.title && (
              <span style={titleStyle}>{announcement.title}</span>
            )}
            {bodyText && (
              <span style={bodyStyle}>{bodyText}</span>
            )}
            {announcement.cta?.url && announcement.cta?.text && (
              <a
                href={announcement.cta.url}
                style={ctaStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {announcement.cta.text}
                <span style={{ fontSize: 11 }}>→</span>
              </a>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {announcement.is_dismissible && (
            <button
              aria-label="Dismiss announcement"
              onClick={onDismiss}
              style={dismissButtonStyle}
              title="Dismiss"
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.7";
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
